import React, { useState, useEffect } from 'react';
import { MdClose, MdSearch, MdAdd } from 'react-icons/md';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';

/**
 * Add Subject Manually Modal
 * Allows faculty admins to add multiple subjects at once
 */
function AddSubjectModal({ 
  isOpen, 
  onClose, 
  onAdd, 
  batchIds, 
  campus,
  facultyId,
  semester: initialSemester,
  existingSubjectIds = [] 
}) {
  const { getToken } = useAttendanceAuth();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(initialSemester || '');

  useEffect(() => {
    if (isOpen && campus && batchIds.length > 0) {
      // Reset semester when modal opens
      setSelectedSemester(initialSemester || '');
      setSelectedSubjects([]);
      setSearchTerm('');
      setSubjects([]);
    }
  }, [isOpen, campus, batchIds, initialSemester]);

  useEffect(() => {
    // Reload subjects when semester changes
    if (isOpen && campus && batchIds.length > 0 && selectedSemester) {
      loadSubjects();
    } else if (isOpen && batchIds.length > 0 && !selectedSemester) {
      setSubjects([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSemester, isOpen, campus, batchIds]);

  const loadSubjects = async () => {
    if (!selectedSemester) {
      setSubjects([]);
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      const result = await attendanceAPI.getSubjects({
        batch_ids: batchIds,
        semester: selectedSemester,
        campus,
        faculty_id: facultyId ? Number(facultyId) : undefined
      }, token);

      if (result.success && result.subjects) {
        // Filter out subjects that are already added
        const availableSubjects = result.subjects.filter(subject => {
          const subjectId = subject.subject_id || subject.id;
          return !existingSubjectIds.includes(subjectId);
        });
        setSubjects(availableSubjects);
      } else {
        setSubjects([]);
      }
    } catch (err) {
      console.error('Error loading subjects:', err);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubjects = subjects.filter(subject => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = (subject.name || subject.subject_name || '').toLowerCase();
    const code = (subject.code || '').toLowerCase();
    return name.includes(term) || code.includes(term);
  });

  const handleToggleSubject = (subject) => {
    const subjectId = subject.subject_id || subject.id;
    setSelectedSubjects(prev => {
      const isSelected = prev.some(s => (s.subject_id || s.id) === subjectId);
      if (isSelected) {
        return prev.filter(s => (s.subject_id || s.id) !== subjectId);
      } else {
        return [...prev, subject];
      }
    });
  };

  const handleAdd = () => {
    if (selectedSubjects.length > 0) {
      const newSubjects = selectedSubjects.map(selectedSubject => ({
        subject_id: selectedSubject.subject_id || selectedSubject.id,
        name: selectedSubject.name || selectedSubject.subject_name,
        subject_name: selectedSubject.subject_name || selectedSubject.name,
        tutor_id: selectedSubject.tutor_id || null,
        tutor_assignment_id: selectedSubject.tutor_assignment_id || null,
        is_shared: Boolean(selectedSubject.is_shared),
        tutor_name: selectedSubject.tutor_name || selectedSubject.tutor || 'Not Assigned',
        campus: selectedSubject.campus || campus,
        // Initialize empty period days arrays
        period_1_days: [],
        period_2_days: [],
        period_3_days: [],
        period_4_days: [],
        period_5_days: [],
        period_6_days: [],
        period_7_days: [],
        period_8_days: []
      }));
      onAdd(newSubjects);
      setSelectedSubjects([]);
      setSearchTerm('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Add Subject Manually</h2>
            {selectedSubjects.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {selectedSubjects.length} subject{selectedSubjects.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!batchIds.length ? (
            <div className="text-center py-8 text-gray-500">
              <p>Please select batches first</p>
            </div>
          ) : (
            <>
              {/* Semester Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Semester <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Semester</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(sem => (
                    <option key={sem} value={sem}>Semester {sem}</option>
                  ))}
                </select>
              </div>

              {!selectedSemester ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Please select a semester to view subjects</p>
                </div>
              ) : loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading subjects...</p>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search subjects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Subject List */}
              {filteredSubjects.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No available subjects found</p>
                  {existingSubjectIds.length > 0 && (
                    <p className="text-sm mt-2">All subjects may already be added</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredSubjects.map((subject) => {
                    const subjectId = subject.subject_id || subject.id;
                    const isSelected = selectedSubjects.some(s => (s.subject_id || s.id) === subjectId);
                    
                    return (
                      <div
                        key={subjectId}
                        onClick={() => handleToggleSubject(subject)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-800">
                              {subject.name || subject.subject_name}
                            </h3>
                            {subject.code && (
                              <p className="text-sm text-gray-500 mt-1">Code: {subject.code}</p>
                            )}
                            {subject.tutor_name && (
                              <p className="text-sm text-gray-600 mt-1">
                                Tutor: {subject.tutor_name}
                              </p>
                            )}
                          </div>
                          <div className="ml-4 flex items-center">
                            <div className={`w-6 h-6 border-2 rounded flex items-center justify-center transition ${
                              isSelected
                                ? 'bg-blue-500 border-blue-500'
                                : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <span className="text-white text-xs">✓</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selectedSubjects.length === 0}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdAdd />
            <span>Add {selectedSubjects.length > 0 ? `${selectedSubjects.length} Subject${selectedSubjects.length > 1 ? 's' : ''}` : 'Subject'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddSubjectModal;

